import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

const SIZES = {
  sm: 'w-full sm:max-w-sm',
  md: 'w-full sm:max-w-md',
  lg: 'w-full sm:max-w-lg',
  xl: 'w-full sm:max-w-xl',
};

export function Drawer({
  open,
  onClose,
  side = 'right',
  size = 'md',
  title,
  description,
  children,
  className,
  hideClose = false,
  closeOnBackdrop = true,
}) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    if (open) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        aria-hidden={!open}
        onClick={() => closeOnBackdrop && onClose?.()}
        className={cn(
          'fixed inset-0 z-[60] transition-opacity duration-300',
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
        style={{
          background: 'rgba(4, 2, 10, 0.55)',
          backdropFilter: 'blur(6px)',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'fixed top-0 z-[61] flex h-full flex-col transition-transform duration-300 ease-out',
          side === 'right' ? 'right-0' : 'left-0',
          SIZES[size],
          className
        )}
        style={{
          background: 'var(--dash-surface)',
          borderLeft: side === 'right' ? '1px solid var(--dash-border)' : 'none',
          borderRight: side === 'left' ? '1px solid var(--dash-border)' : 'none',
          boxShadow: '0 0 64px rgba(0,0,0,0.45)',
          transform: open
            ? 'translateX(0)'
            : side === 'right'
              ? 'translateX(100%)'
              : 'translateX(-100%)',
        }}
      >
        {(title || !hideClose) && (
          <div
            className="flex items-start justify-between gap-4 border-b px-6 py-5"
            style={{ borderColor: 'var(--dash-border)' }}
          >
            <div className="flex flex-col gap-1">
              {title && (
                <h2
                  className="text-base font-semibold tracking-tight"
                  style={{ color: 'var(--dash-text)' }}
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  className="text-xs"
                  style={{ color: 'var(--dash-text-muted)' }}
                >
                  {description}
                </p>
              )}
            </div>
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors"
                style={{
                  background: 'var(--dash-surface-subtle)',
                  border: '1px solid var(--dash-border)',
                  color: 'var(--dash-text-soft)',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
        <div className="dash-scroll flex-1 overflow-y-auto">{children}</div>
      </div>
    </>,
    document.body
  );
}
